function getComponentsPerPixel(gl: WebGL2RenderingContext, internalFormat: GLenum) {
	const ext = gl.getExtension("EXT_texture_norm16")

	switch (internalFormat) {
		// Luminance/alpha
		case gl.LUMINANCE_ALPHA:
		case gl.LUMINANCE:
		case gl.ALPHA:
			return 1

		// Red
		case gl.R8:
		case gl.R8I:
		case gl.R8UI:
		case gl.R8_SNORM:
		case gl.R16F:
		case gl.R16I:
		case gl.R16UI:
		case gl.R32F:
		case gl.R32I:
		case gl.R32UI:
			return 1

		// Red,Green
		case gl.RG:
		case gl.RG8:
		case gl.RG8I:
		case gl.RG8UI:
		case gl.RG8_SNORM:
		case gl.RG16F:
		case gl.RG16I:
		case gl.RG16UI:
		case gl.RG32F:
		case gl.RG32I:
		case gl.RG32UI:
			return 2

		// Red,Green,Blue
		case gl.RGB:
		case gl.RGB8:
		case gl.RGB8I:
		case gl.RGB8UI:
		case gl.RGB8_SNORM:
		case gl.RGB16F:
		case gl.RGB16I:
		case gl.RGB16UI:
		case gl.RGB32F:
		case gl.RGB32I:
		case gl.RGB32UI:
			return 3

		// Red,Green,Blue,Alpha
		case gl.RGBA:
		case gl.RGBA8:
		case gl.RGBA8I:
		case gl.RGBA8UI:
		case gl.RGBA8_SNORM:
		case gl.RGBA16F:
		case gl.RGBA16I:
		case gl.RGBA16UI:
		case gl.RGBA32F:
		case gl.RGBA32I:
		case gl.RGBA32UI:
			return 4

		// sRGB
		case gl.SRGB8:
			return 3
		case gl.SRGB8_ALPHA8:
			return 4

		// =======================
		// === Special formats ===
		// =======================

		case gl.RGB565:
		case gl.R11F_G11F_B10F:
		case gl.RGB9_E5: // 14-bits packed float
			return 3

		case gl.RGB5_A1:
		case gl.RGB10_A2:
		case gl.RGBA4:
			return 4

		// =================
		// === Extension ===
		// =================

		case ext.R16_EXT:
		case ext.R16_SNORM_EXT:
			return 1

		case ext.RG16_EXT:
		case ext.RG16_SNORM_EXT:
			return 2

		case ext.RGB16_EXT:
		case ext.RGB16_SNORM_EXT:
			return 3

		case ext.RGBA16_EXT:
		case ext.RGBA16_SNORM_EXT:
			return 4

		default:
			return 4
	}
}

export function getBytesPerPixel(gl: WebGL2RenderingContext, internalFormat: GLenum) {
	const ext = gl.getExtension("EXT_texture_norm16")
	const components = getComponentsPerPixel(gl, internalFormat)

	switch (internalFormat) {
		// =====================
		// === 8-bit formats ===
		// =====================

		// Luminance/alpha
		case gl.LUMINANCE_ALPHA:
		case gl.LUMINANCE:
		case gl.ALPHA:
			return components

		// Red
		case gl.R8:
		case gl.R8I:
		case gl.R8UI:
		case gl.R8_SNORM:
			return components

		// Red,Green
		case gl.RG:
		case gl.RG8:
		case gl.RG8I:
		case gl.RG8UI:
		case gl.RG8_SNORM:
			return components

		// Red,Green,Blue
		case gl.RGB:
		case gl.RGB8:
		case gl.RGB8I:
		case gl.RGB8UI:
		case gl.RGB8_SNORM:
			return components

		// Red,Green,Blue,Alpha
		case gl.RGBA:
		case gl.RGBA8:
		case gl.RGBA8I:
		case gl.RGBA8UI:
		case gl.RGBA8_SNORM:
			return components

		// sRGB
		case gl.SRGB8:
		case gl.SRGB8_ALPHA8:
			return components

		// ======================
		// === 16-bit formats ===
		// ======================

		// 16-bit float
		case gl.R16F:
		case gl.RG16F:
		case gl.RGB16F:
		case gl.RGBA16F:
			return components * 2

		// 16-bit int
		case gl.R16I:
		case gl.RG16I:
		case gl.RGB16I:
		case gl.RGBA16I:
			return components * 2

		// 16-bit uint
		case gl.R16UI:
		case gl.RG16UI:
		case gl.RGB16UI:
		case gl.RGBA16UI:
			return components * 2

		// 16-bit extension formats
		case ext.R16_EXT:
		case ext.RG16_EXT:
		case ext.RGB16_EXT:
		case ext.RGBA16_EXT:
		case ext.R16_SNORM_EXT:
		case ext.RG16_SNORM_EXT:
		case ext.RGB16_SNORM_EXT:
		case ext.RGBA16_SNORM_EXT:
			return components * 2

		// ======================
		// === 32-bit formats ===
		// ======================

		// 32-bit float
		case gl.R32F:
		case gl.RG32F:
		case gl.RGB32F:
		case gl.RGBA32F:
			return components * 4

		// 32-bit int
		case gl.R32I:
		case gl.RG32I:
		case gl.RGB32I:
		case gl.RGBA32I:
			return components * 4

		// 32-bit uint
		case gl.R32UI:
		case gl.RG32UI:
		case gl.RGB32UI:
		case gl.RGBA32UI:
			return components * 4

		// =======================
		// === Special formats ===
		// =======================

		case gl.RGB565:
			return 2
		case gl.R11F_G11F_B10F:
			return 4
		case gl.RGB9_E5: // 14-bits packed float
			return 4 * 2
		case gl.RGB5_A1:
			return 2
		case gl.RGB10_A2:
			return 4
		case gl.RGBA4:
			return 2

		default:
			return 4
	}
}
